import { object } from '@art-suite/art-core-ts';

import { getUniqueId } from '../commonLib';
import { InlineProductsById } from '../CommonScanSchemas';
import {
  getAllScanTaskTemplatesForGroup,
  getInlineProductFromSkuOption,
  getSkuIdFromSkuOption,
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplatePart,
  ScanTemplateRoot,
  ScanTemplateTask,
} from '../ScanTemplateRoot';
import { ScanGroup, ScanHomeFacts, ScanLineItem, ScanOption, ScanPart, ScanRoot, ScanTask } from './ScanRootSchemas';

export const isTemplatePartIncludedByDefault = (template: ScanTemplatePart | undefined): boolean => !!template && !template.hidden;

// **************************************************************
// Deoptimized Scan INITIALIZATION
// **************************************************************

export const initCommonDeoptimizedScanPartProps = <TemplateType extends ScanTemplatePart>(template: TemplateType, now: number = Date.now()): ScanPart => {
  const result: ScanPart = {
    id: getUniqueId(),
    createdAt: now,
    updatedAt: now,
    templateId: template.id,
    values: object(template.values, () => ({})),
  };

  // auto select the first SKU option if there is only one, whether the template names the
  // product by id or carries the whole product inline
  const [onlySkuOption] = template.skuIdOptions?.length === 1 ? template.skuIdOptions : [];
  if (onlySkuOption) result.skuId = getSkuIdFromSkuOption(onlySkuOption);
  return result;
};

/**
 * A line item from its template. A template offering exactly one SKU also takes that product's
 * details and its price, the live product when `inlineProductsById` carries it, otherwise the copy
 * in the template. The price is set once, as `price.setValue`; a later catalog price change is
 * presented to the user to accept.
 */
export const initDeoptimizedLineItem = (template: ScanTemplateLineItem, now: number = Date.now(), inlineProductsById?: InlineProductsById): ScanLineItem => {
  const lineItem: ScanLineItem = { ...initCommonDeoptimizedScanPartProps(template, now) };
  const { skuId } = lineItem; // set only when the template offers exactly one SKU
  const skuDetails = skuId && (inlineProductsById?.[skuId] ?? getInlineProductFromSkuOption(template.skuIdOptions![0]));
  if (skuDetails) {
    lineItem.skuDetails = skuDetails;
    if (lineItem.values?.price) lineItem.values.price.setValue = skuDetails.price;
  }
  return lineItem;
};

export const initDeoptimizedOption = (template: ScanTemplateOption, now: number = Date.now(), inlineProductsById?: InlineProductsById): ScanOption => ({
  ...initCommonDeoptimizedScanPartProps(template, now),
  lineItems: (template.lineItemTemplates || [])
    .filter(isTemplatePartIncludedByDefault)
    .map(lineItemTemplate => initDeoptimizedLineItem(lineItemTemplate, now, inlineProductsById)),
});

export const initDeoptimizedTask = (template: ScanTemplateTask, now: number = Date.now()): ScanTask => ({
  ...initCommonDeoptimizedScanPartProps(template, now),
  options: [],
});

export const initDeoptimizedGroup = (rootTemplate: ScanTemplateRoot, groupTemplate: ScanTemplateGroup, now: number = Date.now()): ScanGroup => ({
  ...initCommonDeoptimizedScanPartProps(groupTemplate, now),
  tasks: getAllScanTaskTemplatesForGroup(rootTemplate, groupTemplate)
    .filter(isTemplatePartIncludedByDefault)
    .map(taskTemplate => initDeoptimizedTask(taskTemplate, now)),
});

const MAX_BATHROOM_COUNT = 10;
const MAX_BEDROOM_COUNT = 10;

export const initDeoptimizedScan = (rootTemplate: ScanTemplateRoot, now: number = Date.now(), homeFacts?: ScanHomeFacts): ScanRoot => {
  const groupTemplatesBySubtype: Record<string, ScanTemplateGroup> = {};
  if (rootTemplate.groupTemplates) {
    rootTemplate.groupTemplates.forEach(groupTemplate => {
      if (groupTemplate.subtype) {
        groupTemplatesBySubtype[groupTemplate.subtype] = groupTemplate;
      }
    });
  }

  let bedroomCount = homeFacts?.bedroomCount;
  let bathroomCount = homeFacts?.bathroomCount;
  if (bedroomCount && bedroomCount > MAX_BEDROOM_COUNT) {
    bedroomCount = MAX_BEDROOM_COUNT;
  }
  if (bathroomCount && bathroomCount > MAX_BATHROOM_COUNT) {
    bathroomCount = MAX_BATHROOM_COUNT;
  }

  return ({
    ...initCommonDeoptimizedScanPartProps(rootTemplate, now),
    groups: (rootTemplate.groupTemplates || [])
      .filter(isTemplatePartIncludedByDefault)
      .map(groupTemplate => {
        const groups: ScanGroup[] = [];
        if (bedroomCount && groupTemplate.subtype === 'otherBedroom') {
          // Create other bedroom groups for bedrooms 2 through bedroomCount
          const startCountingFrom = groupTemplatesBySubtype.mainBedroom ? 2 : 1;
          for (let i = startCountingFrom; i <= bedroomCount; i++) {
            const group = initDeoptimizedGroup(rootTemplate, groupTemplate, now);
            group.customLabel = `${i}`;
            groups.push(group);
          }
        } else if (bathroomCount && groupTemplate.subtype === 'otherBathroom') {
          // Create other bathroom groups for bathrooms 2 through bathroomCount
          const startCountingFrom = groupTemplatesBySubtype.mainBathroom ? 2 : 1;
          for (let i = startCountingFrom; i <= bathroomCount; i++) {
            const group = initDeoptimizedGroup(rootTemplate, groupTemplate, now);
            group.customLabel = `${i}`;
            groups.push(group);
          }
        } else {
          groups.push(initDeoptimizedGroup(rootTemplate, groupTemplate, now));
        }
        return groups;
      }).flat(),
  });
};
